import React, { useEffect, useState, useRef } from "react";
import { Layout, message, Tooltip, Popconfirm } from "antd";
import { EditOutlined, DeleteOutlined, SwapOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchJobPostings,
  fetchSingleJobPosting,
  deleteJobPosting,
  changeJobPostingStatus,
} from "../../../Store/Slices/Admin/jobPosting/jobPostingThunk";
import AdminDashLayout from "../../../Components/Admin/AdminDashLayout";
import {
  BiBriefcase,
  BiGroup,
  BiCalendar,
  BiCheckCircle,
  BiSearch,
  BiTime,
  BiGlobe,
  BiMap,
} from "react-icons/bi";
import ProtectedSection from "../../../Routes/ProtectedRoutes/ProtectedSection";
import { PERMISSIONS } from "../../../config/permission";
import ProtectedAction from "../../../Routes/ProtectedRoutes/ProtectedAction";
import useNavHeading from "../../../Hooks/CommonHooks/useNavHeading ";
import { useTranslation } from "react-i18next";
import JobDetailPanel from "./JobDetails";
import Sidebar from "../../../Components/Common/Sidebar";

const CareersMain = () => {
  useNavHeading("Carriers");
  const { t } = useTranslation("common");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { jobs, loading, singleJob } = useSelector((state) => state.admin.jobPosting);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelLoading, setPanelLoading] = useState(false);
  const isFirstRender = useRef(true);

  // Initial fetch on mount (immediate, no debounce)
  useEffect(() => {
    dispatch(fetchJobPostings());
  }, [dispatch]);

  // Debounced search + filter re-fetch (skips first render to avoid double fetch)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const params = {};
    if (search) params.search = search;
    if (statusFilter !== "all") params.status = statusFilter;

    const delay = search ? 500 : 0;
    const timer = setTimeout(() => {
      dispatch(fetchJobPostings(Object.keys(params).length ? params : undefined));
    }, delay);

    return () => clearTimeout(timer);
  }, [search, statusFilter, dispatch]);

  const handleChangeStatus = async (record) => {
    try {
      await dispatch(
        changeJobPostingStatus({
          id: record._id,
          status: record.status === "published" ? "closed" : "published",
        })
      ).unwrap();
      message.success(t("Job status updated successfully"));
    } catch (error) {
      message.error(t("Failed to update job status"));
    }
  };

  const handleViewDetails = async (jobId) => {
    setPanelOpen(true);
    setPanelLoading(true);
    await dispatch(fetchSingleJobPosting(jobId)).unwrap().catch(() => { });
    setPanelLoading(false);
  };

  const handleClosePanel = () => {
    setPanelOpen(false);
  };

  // Derived stats
  const activeCount = jobs.filter((j) => j.status === "published").length;
  const draftCount = jobs.filter((j) => j.status === "draft").length;
  const closedCount = jobs.filter((j) => j.status === "closed").length;

  const statCards = [
    {
      label: t("Active Openings"),
      value: activeCount,
      subtitle: t("Currently hiring"),
      icon: <BiBriefcase className="text-blue-600 text-2xl" />,
      iconBg: "bg-blue-100",
    },
    {
      label: t("Total Jobs"),
      value: jobs.length,
      subtitle: t("Across all roles"),
      icon: <BiGroup className="text-purple-600 text-2xl" />,
      iconBg: "bg-purple-100",
    },
    {
      label: t("Draft Jobs"),
      value: draftCount,
      subtitle: t("Pending review"),
      icon: <BiCalendar className="text-orange-500 text-2xl" />,
      iconBg: "bg-orange-100",
    },
    {
      label: t("Closed Jobs"),
      value: closedCount,
      subtitle: t("Filled positions"),
      icon: <BiCheckCircle className="text-green-600 text-2xl" />,
      iconBg: "bg-green-100",
    },
  ];

  const filterButtons = [
    { key: "all", label: t("ALL") },
    { key: "published", label: t("OPEN") },
    { key: "closed", label: t("CLOSED") },
    { key: "draft", label: t("DRAFT") },
  ];

  const statusConfig = {
    published: {
      label: t("OPEN"),
      className: "bg-green-50 text-green-600 border border-green-200",
    },
    closed: {
      label: t("CLOSED"),
      className: "bg-gray-100 text-gray-500 border border-gray-200",
    },
    draft: {
      label: t("DRAFT"),
      className: "bg-orange-50 text-orange-500 border border-orange-200",
    },
  };

  return (
    <>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      <Layout>
        <AdminDashLayout>
          <ProtectedSection
            requiredPermission={PERMISSIONS.VIEW_JOB_POSTING}
            title={"job posting"}
          >
            <div className="relative flex min-h-screen bg-gray-50">

              {/* ── Main content area (shrinks when panel open) ── */}
              <div
                className="flex-1 p-4 transition-all duration-300"
                style={{ width: panelOpen ? "30%" : "100%" }}
              >

                {/* ── Header ───────────────────────────────────── */}
                <div className="flex flex-wrap justify-between items-center gap-4 mb-8 bg-white rounded-xl shadow-sm p-4">
                  <div>
                    <h1 className="page-heading">
                      {t("Recruitment")}
                    </h1>
                    <p className="subheading">
                      {t("Manage job openings, applications, and hiring processes.")}
                    </p>
                  </div>

                  <ProtectedAction requiredPermission={PERMISSIONS.CREATE_JOB}>
                    <button
                      className="btn-gradient flex items-center gap-2 !px-5 !py-2 !rounded-xl !text-sm !font-semibold"
                      onClick={() => navigate("/careers/create")}
                    >
                      <span className="text-lg leading-none">+</span>
                      {t("Post New Job")}
                    </button>
                  </ProtectedAction>
                </div>

                {/* ── Stat Cards ───────────────────────────────── */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  {statCards.map((card, i) => (
                    <div
                      key={i}
                      className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
                      style={{ animation: `fadeInUp 0.4s ease ${i * 0.08}s both` }}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-11 h-11 rounded-xl ${card.iconBg} flex items-center justify-center flex-shrink-0`}>
                          {card.icon}
                        </div>
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider leading-tight">
                          {card.label}
                        </span>
                      </div>
                      <div className="text-3xl font-bold text-gray-900">
                        {loading ? (
                          <span className="inline-block w-8 h-7 bg-gray-200 rounded animate-pulse" />
                        ) : (
                          card.value
                        )}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">{card.subtitle}</div>
                    </div>
                  ))}
                </div>

                {/* ── Search + Filter ──────────────────────────── */}
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                  <div className="flex-1 relative">
                    <BiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg pointer-events-none" />
                    <input
                      type="text"
                      placeholder={t("Search by job title or department...")}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 bg-white focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100 text-gray-700 placeholder-gray-400 text-sm transition-all"
                    />
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    {filterButtons.map((btn) =>
                      statusFilter === btn.key ? (
                        <button
                          key={btn.key}
                          onClick={() => setStatusFilter(btn.key)}
                          className="btn-gradient !px-5 !py-2 !rounded-xl !text-sm !font-semibold"
                        >
                          {btn.label}
                        </button>
                      ) : (
                        <button
                          key={btn.key}
                          onClick={() => setStatusFilter(btn.key)}
                          className="px-5 py-2 rounded-xl text-sm font-semibold border border-gray-200 bg-white text-gray-600 hover:border-purple-300 hover:text-purple-600 transition-all"
                        >
                          {btn.label}
                        </button>
                      )
                    )}
                  </div>
                </div>

                {/* ── Job Cards Grid ───────────────────────────── */}
                {loading ? (
                  <div className={`grid gap-4 ${panelOpen ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"}`}>
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 animate-pulse">
                        <div className="flex justify-between mb-4">
                          <div className="h-5 bg-gray-200 rounded-full w-20" />
                          <div className="h-5 bg-gray-200 rounded-full w-14" />
                        </div>
                        <div className="h-6 bg-gray-200 rounded w-3/4 mb-3" />
                        <div className="h-4 bg-gray-200 rounded w-1/2 mb-6" />
                        <div className="border-t border-gray-100 pt-4 flex justify-between">
                          <div className="flex gap-2">
                            <div className="h-8 w-8 bg-gray-200 rounded-lg" />
                            <div className="h-8 w-8 bg-gray-200 rounded-lg" />
                            <div className="h-8 w-8 bg-gray-200 rounded-lg" />
                          </div>
                          <div className="flex gap-2">
                            <div className="h-8 w-24 bg-gray-200 rounded-xl" />
                            <div className="h-8 w-32 bg-gray-200 rounded-xl" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : jobs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                    <BiBriefcase className="text-7xl mb-4 text-gray-200" />
                    <p className="text-lg font-medium">{t("No job postings found")}</p>
                    <p className="text-sm mt-1">{t("Try adjusting your search or filters")}</p>
                  </div>
                ) : (
                  <div className={`grid gap-4 ${panelOpen ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"}`}>
                    {jobs.map((job, i) => {
                      const status =
                        statusConfig[job.status] || {
                          label: job.status,
                          className: "bg-gray-100 text-gray-500 border border-gray-200",
                        };
                      const isSelected = panelOpen && singleJob?._id === job._id;
                      return (
                        <div
                          key={job._id}
                          className={`bg-white rounded-2xl p-6 shadow-sm border transition-all duration-300
                            ${isSelected
                              ? "border-purple-300 shadow-purple-100 shadow-md"
                              : "border-gray-100 hover:shadow-xl hover:-translate-y-1"
                            }`}
                          style={{ animation: `fadeInUp 0.4s ease ${i * 0.07}s both` }}
                        >
                          {/* Top: department badge + status badge */}
                          <div className="flex justify-between items-center mb-3">
                            {job.department ? (
                              <span className="text-xs font-semibold text-purple-600 bg-purple-50 px-3 py-1 rounded-full uppercase tracking-wider border border-purple-100">
                                {job.department}
                              </span>
                            ) : (
                              <span />
                            )}
                            <span className={`text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider ${status.className}`}>
                              {status.label}
                            </span>
                          </div>

                          {/* Job title */}
                          <h3 className="text-xl font-bold text-gray-900 mb-3 capitalize">
                            {job.title}
                          </h3>

                          {/* Meta row */}
                          <div className="flex items-center gap-4 text-gray-400 text-sm mb-5">
                            <span className="flex items-center gap-1.5">
                              <BiTime className="text-base flex-shrink-0" />
                              <span className="capitalize">{job.employmentType}</span>
                            </span>
                            <BiGlobe className="text-base" />
                            <BiMap className="text-base" />
                          </div>

                          {/* Divider + Actions */}
                          <div className="border-t border-gray-100 pt-4">
                            <div className="flex items-center justify-between gap-2 flex-wrap">

                              {/* Icon actions: Edit, Toggle, Delete */}
                              <div className="flex items-center gap-2">
                                <ProtectedAction requiredPermission={PERMISSIONS.EDIT_JOB_POSTING}>
                                  <Tooltip title={t("Edit Job")}>
                                    <button
                                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-purple-400 hover:text-purple-600 transition-all text-sm"
                                      onClick={() => navigate(`/careers/edit/${job._id}`)}
                                    >
                                      <EditOutlined />
                                    </button>
                                  </Tooltip>
                                </ProtectedAction>

                                <ProtectedAction requiredPermission={PERMISSIONS.EDIT_JOB_POSTING_STATUS}>
                                  <Tooltip title={t("Toggle Status")}>
                                    <button
                                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-purple-400 hover:text-purple-600 transition-all text-sm"
                                      onClick={() => handleChangeStatus(job)}
                                    >
                                      <SwapOutlined />
                                    </button>
                                  </Tooltip>
                                </ProtectedAction>

                                <ProtectedAction requiredPermission={PERMISSIONS.DELETE_JOB}>
                                  <Popconfirm
                                    title={
                                      <div className="max-w-xs">
                                        <p className="text-red-600 font-semibold text-sm mb-1">
                                          ⚠️ Delete Job Posting
                                        </p>
                                        <p className="text-gray-600 text-xs leading-relaxed">
                                          This action will permanently delete:
                                        </p>

                                        <ul className="text-gray-500 text-xs list-disc pl-4 mt-1 space-y-0.5">
                                          <li>Applications</li>
                                          <li>Interview rounds</li>
                                          <li>Candidate records</li>
                                        </ul>
                                      </div>
                                    }
                                    onConfirm={() => dispatch(deleteJobPosting(job._id))}
                                    okText={t("Yes")}
                                    cancelText={t("No")}
                                  >
                                    <Tooltip title={t("Delete Job")}>
                                      <button className="w-8 h-8 flex items-center justify-center rounded-lg border border-red-200 text-red-400 hover:border-red-400 hover:text-red-600 transition-all text-sm">
                                        <DeleteOutlined />
                                      </button>
                                    </Tooltip>
                                  </Popconfirm>
                                </ProtectedAction>
                              </div>

                              {/* Main buttons */}
                              <div className="flex items-center gap-2">
                                <ProtectedAction requiredPermission={PERMISSIONS.VIEW_JOB_POSTING}>
                                  <button
                                    className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-all whitespace-nowrap
                                      ${isSelected
                                        ? "border-purple-400 text-purple-600 bg-purple-50"
                                        : "border-gray-200 text-gray-600 hover:border-purple-300 hover:text-purple-600"
                                      }`}
                                    onClick={() => handleViewDetails(job._id)}
                                  >
                                    {t("View Details")}
                                  </button>
                                </ProtectedAction>

                                <ProtectedAction requiredPermission={PERMISSIONS.VIEW_JOB_APPLICANTS}>
                                  <button
                                    className="btn-gradient !px-4 !py-2 !rounded-xl !text-sm !font-semibold whitespace-nowrap"
                                    onClick={() => navigate(`/careers/applications/${job._id}`)}
                                  >
                                    {t("Manage Applicants")}
                                  </button>
                                </ProtectedAction>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ── Detail Panel (70% width, slides in from right) ── */}
              {panelOpen && (

                <Sidebar
                  isOpen={panelOpen}
                  onClose={() => {
                    setPanelOpen(false)
                  }}
                  title={t("Job Details")}
                  width={900}
                >
                  <JobDetailPanel
                    job={singleJob}
                    loading={panelLoading}
                    onClose={handleClosePanel}
                    onEdit={() => navigate(`/careers/edit/${singleJob?._id}`)}
                    onManageApplicants={() => navigate(`/careers/applications/${singleJob?._id}`)}
                  />
                </Sidebar>
              )}
            </div>
          </ProtectedSection>
        </AdminDashLayout>
      </Layout>
    </>
  );
};

export default CareersMain;
